import * as React from "react";
import {Static} from "@bizform/type/decorator/Static.decorator";
import {DomAttribute} from "@bizform/type/model/dom/DomAttribute.model";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementBorderType,
    ComponentElementDisplayMode,
    ComponentElementOverflowType,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import {WindowKey} from "@bizform/type/model/model/WindowKey.model";
import {ExceptionType} from "@bizform/type/model/model/Exception.model";
import {ComponentHelper, Debug} from "@bizform/helper/dist/js/bizflow/helper.min";
import {
    ComponentElementClass,
    ExceptionHandler,
    ReactComponentElementInit
} from "@bizform/core/dist/js/bizflow/core.min";
import {AbstractComponentElement} from "../../bf_component/AbstractComponentElement";
import {I18n} from "../../misc/model/I18n.model";


type WrappedCompState = ComponentElementOverflowType & ComponentElementBorderType & {
    height?: string;
    scroll?: boolean;
    className?: string;
    style?: any;
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

/**
 * @since 0.0.1
 * @author Hwansoon Park
 * @class BfScrollPanel
 * @description BfScrollPanel component element
 */
@ComponentElementClass()
@Static<ComponentElementStatic<null>>()
export class BfScrollPanel extends AbstractComponentElement<null, State> {
    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_Bl08vKLDcKqcP94pH9iNOvLg0wbkSaw",
        category: ComponentCategory.PANEL,
        label: I18n.layout.scrollPanel.label,
        description: I18n.layout.scrollPanel.description,
        icon: "",
        property: null
    };

    /* for React */
    state: State;
    private _panel: HTMLElement;
    private _init: boolean;

    constructor(props?: any) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {}
        };

        this._setRef = this._setRef.bind(this);
    }

    @ReactComponentElementInit(BfScrollPanel)
    static init(): ComponentElement<null> {
        return;
    }

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, { e: Event, value?: boolean }>(
                property,
                this);
            if (!newState.wrappedCompState.style) {
                newState.wrappedCompState.style = {};
            }
            if (this.accessibility) {
                delete((newState.wrappedCompState.style as any).height);
                delete((newState.wrappedCompState.style as any).overflowY);
            } else {
                if (newState.wrappedCompState.height) {
                    newState.wrappedCompState.style.height = newState.wrappedCompState.height;
                }
                if (newState.wrappedCompState.scroll || window[WindowKey.DESIGNMODE]) {
                    if (window[WindowKey.DESIGNMODE] && this._init && !newState.wrappedCompState.scroll) {
                        ExceptionHandler.toast(ExceptionType.INFO, `Scroll bar values of the Scroll Panel are always "true" at design time.`);
                    }
                    newState.wrappedCompState.style.overflowY = "auto";
                } else {
                    newState.wrappedCompState.style.overflowY = "hidden";
                }
                delete(newState.wrappedCompState.scroll);
            }
            this._init = true;
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    componentWillUnmount(): void {
        this._panel = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        if (!this.state.show) {
            return null;
        } else {
            const bfContainer = {[DomAttribute.CONTAINER]: ""};
            return (
                <div
                    {...bfContainer}
                    {...this.state.wrappedCompState}
                    id={this.elementId}
                    ref={this._setRef}
                >
                </div>
            );
        }
    }

    private _setRef(ref): void {
        if (ref) {
            this._panel = ref;
            this.readyComponent();
        }
    }
}